import React, { useEffect, useState } from 'react';
import {
  subscribe,
  getPageById,
  getForms,
  getProjectsByCompanyId,
  getAllSalesUsersByProjectId,
  updateForm
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import NoDataFound from '../components/layout/NoDataFound';
import Modal from '../../shared/components/ui/Modal';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Multiselect from 'multiselect-react-dropdown';

const FBPages = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const location = useLocation();
  const [Allforms, setAllForms] = useState();
  const [pageId, setPageId] = useState(location.state?.pageID);
  const [openModal, setOpenModal] = useState(false);
  const [cmpId, setCmpId] = useState(location.state?.cmpId);
  const [formID, setFormID] = useState();
  const { collapsed } = useSidebar();
  const [updateData, setUpdateData] = useState({
    project_id: '',
    sales_ids: []
  });
  const [selectedSalesPersons, setSelectedSalesPersons] = useState([]);

  const { data, refetch: refetchProjects } = useQuery({
    queryKey: ['project_byId', cmpId],
    queryFn: () => getProjectsByCompanyId(cmpId),
    enabled: !!cmpId // Ensure the query only runs if companyIdInt is valid
  });

  const { data: sales, refetch: refetchSales } = useQuery({
    queryKey: ['salesUsers', updateData.project_id],
    queryFn: () => getAllSalesUsersByProjectId(updateData.project_id, 1, 500), // Ensure the query only runs if companyIdInt is valid
    enabled: !!updateData.project_id
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('F B Pages Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchProjects();
    refetchSales();
  }, []);

  useEffect(() => {
    if (pageId) {
      PageByID({ pageId });
    }
  }, [pageId]);

  const { mutate: PageByID, refetch: refetchForms } = useMutation({
    mutationFn: ({ pageId }) => getPageById(pageId),
    onSuccess: async (data) => {
      setAllForms(data);
    },
    onError: (err) => {
      console.error(`Error Fetching Forms`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Fetching forms`);
    }
  });

  const handleSubscribe = () => {
    if (!cmpId) {
      toast.error('Company id not found');
      return;
    }
    const body = {
      company_id: cmpId
    };
    Subscribe({ body });
  };

  const { mutate: Subscribe } = useMutation({
    mutationFn: ({ body }) => subscribe(body),
    onSuccess: async (data) => {
      toast.success('Subscribed Successfully');
      if (pageId) {
        PageByID({ pageId });
      }
    },
    onError: (err) => {
      console.error(`Error Subscribing`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Subscribing`);
    }
  });

  const handleGetForm = () => {
    if (!cmpId) {
      toast.error('Company id not found');
      return;
    }
    const body = {
      company_id: cmpId
    };
    GetForms({ body });
  };

  const { mutate: GetForms } = useMutation({
    mutationFn: ({ body }) => getForms(body),
    onSuccess: async (data) => {
      toast.success('Forms Fetched Successfully');
      if (pageId) {
        PageByID({ pageId });
      }
    },
    onError: (err) => {
      console.error(`Error Fetching Forms`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Fetching Forms`);
    }
  });

  const handleUpdateForm = (id) => {
    if (!updateData.sales_ids || updateData.sales_ids.length === 0 || !updateData.project_id) {
      toast.error('Please select all fields');
      return;
    }
    if (!formID) {
      toast.error('Form Id Not Found');
      return;
    }
    const body = {
      project_id: updateData.project_id,
      sales_ids: updateData.sales_ids
    };
    UpdateForm({ formID, body });
  };

  const { mutate: UpdateForm } = useMutation({
    mutationFn: ({ formID, body }) => updateForm(formID, body),
    onSuccess: async (data) => {
      toast.success('Form Updated Successfully');
      if (pageId) {
        PageByID({ pageId });
      }
      setOpenModal(false);
      setUpdateData({
        project_id: '',
        sales_ids: []
      });
      setSelectedSalesPersons([]);
      setFormID(null);
    },
    onError: (err) => {
      console.error(`Error Updating Form`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error Updating Form`);
    }
  });

  const handleShowModal = (d) => {
    const isProject = data?.find((item) => item.id === parseInt(d.project_id));

    if (isProject) {
      setUpdateData((prev) => ({ ...prev, project_id: isProject.id }));
    }

    // Handle sales persons - now using the new sales_persons array from the form data
    if (d.sales_persons && Array.isArray(d.sales_persons) && d.sales_persons.length > 0) {
      // Use the sales_persons data directly from the form
      setSelectedSalesPersons(d.sales_persons);
      setUpdateData((prev) => ({
        ...prev,
        sales_ids: d.sales_persons.map((person) => person.id)
      }));
    } else {
      // Fallback to old format if sales_persons is not available
      if (d.sales_ids && Array.isArray(d.sales_ids)) {
        // If it's already an array of IDs, find the corresponding sales persons
        const selectedSales =
          sales?.data?.filter((person) => d.sales_ids.includes(person.id)) || [];
        setSelectedSalesPersons(selectedSales);
        setUpdateData((prev) => ({ ...prev, sales_ids: d.sales_ids }));
      } else if (d.sales_id) {
        // If it's a single sales_id (legacy format), convert to array
        const selectedSales =
          sales?.data?.filter((person) => person.id === parseInt(d.sales_id)) || [];
        setSelectedSalesPersons(selectedSales);
        setUpdateData((prev) => ({ ...prev, sales_ids: [parseInt(d.sales_id)] }));
      } else {
        setSelectedSalesPersons([]);
        setUpdateData((prev) => ({ ...prev, sales_ids: [] }));
      }
    }

    setOpenModal(true);
    setFormID(d.id);
  };

  return (
    <Layout>
      {/* Header with Back Button */}

      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration/facebook-companies')}
          data={{ name: 'Integrations > Facebook > Pages' }}
        />
      </div>

      <div
        className={`flex z-20 items-center justify-end px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <div className="flex gap-5 cursor-pointer">
          {Allforms && !Allforms.is_subscribed && (
            <Button onClick={handleSubscribe}>Subscribe</Button>
          )}
          <Button onClick={handleGetForm}>Get Forms</Button>
        </div>
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[9rem] w-full h-full bg-gray-100 p-6">
          <div className="grid grid-cols-3 gap-6">
            {Allforms && Allforms.forms?.length > 0 ? (
              Allforms.forms?.map((f) => (
                <div key={f.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-lg">{f.name}</h3>
                    <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium ">
                      <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                      <p>Live</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      handleShowModal(f);
                    }}>
                    Update
                  </Button>
                </div>
              ))
            ) : (
              <NoDataFound disabledBtn={true} screenHeight={false} />
            )}
          </div>

          {openModal && (
            <Modal
              isDirty={true}
              width={60}
              name="Update Form"
              onClose={() => {
                setOpenModal(false);
                setUpdateData({
                  project_id: '',
                  sales_ids: []
                });
                setSelectedSalesPersons([]);
                setFormID(null);
              }}>
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <p>Project</p>
                  <select
                    // disabled={true}
                    className={`w-full p-2 border-[1px] border-stone-300 rounded `}
                    value={updateData.project_id}
                    onChange={(e) => {
                      const newProjectId = e.target.value;
                      setUpdateData((prev) => ({ ...prev, project_id: newProjectId }));
                      // Reset sales persons when project changes
                      setSelectedSalesPersons([]);
                      setUpdateData((prev) => ({ ...prev, sales_ids: [] }));
                    }}>
                    <option value="" disabled>
                      Select a project
                    </option>
                    {data?.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <p>Sales</p>
                  <Multiselect
                    options={sales?.data || []}
                    selectedValues={updateData.project_id ? selectedSalesPersons : []}
                    onSelect={(selectedList) => {
                      setSelectedSalesPersons(selectedList);
                      setUpdateData((prev) => ({
                        ...prev,
                        sales_ids: selectedList.map((person) => person.id)
                      }));
                    }}
                    onRemove={(selectedList) => {
                      setSelectedSalesPersons(selectedList);
                      setUpdateData((prev) => ({
                        ...prev,
                        sales_ids: selectedList.map((person) => person.id)
                      }));
                    }}
                    displayValue="name"
                    placeholder="Select sales persons"
                    style={{
                      chips: {
                        background: '#2A565E',
                        color: 'white'
                      },
                      searchBox: {
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px',
                        background: 'white',
                        color: '#333'
                      },
                      input: {
                        color: '#333'
                      },
                      singleValue: {
                        color: '#333'
                      },
                      placeholder: {
                        color: '#6c757d'
                      },
                      option: {
                        color: '#333',
                        background: 'white'
                      },
                      listItem: {
                        color: '#333',
                        background: 'white'
                      }
                    }}
                  />
                </div>
              </div>

              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button
                  type="button"
                  onClick={() => {
                    setOpenModal(false);
                    setUpdateData({
                      project_id: '',
                      sales_ids: []
                    });
                    setSelectedSalesPersons([]);
                    setFormID(null);
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button onClick={handleUpdateForm}>Submit</Button>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FBPages;
